/**
 * 物料借用 API 工厂函数
 * 
 * @module api/material-borrow
 * @description 物料借用和归还相关的 API 接口
 */

import type { IRequestClient } from '../core/client'
import type {
  MaterialBorrowResponse,
  MaterialBorrowStatisticsResponse,
} from '../../types/material-borrow/responses'
import type {
  CreateMaterialBorrowRequest,
  ReturnMaterialRequest,
} from '../../types/material-borrow/requests'
import type {
  MaterialBorrowListQuery,
  MyBorrowsQuery,
} from '../../types/material-borrow/queries'
import type { PaginationResult } from '../../types/api/responses'
import { API_V1_ROUTES } from '../routes/v1'

/** 物料借用 API 路由 */
const ROUTES = API_V1_ROUTES.materialBorrow

/**
 * 物料借用 API 接口定义
 */
export interface MaterialBorrowApi {
  /** 获取借用记录列表 */
  getList: (params?: MaterialBorrowListQuery) => Promise<PaginationResult<MaterialBorrowResponse>>
  /** 获取借用记录详情 */
  getDetail: (id: number) => Promise<MaterialBorrowResponse>
  /** 创建借用记录（员工申请/仓管直接出库） */
  create: (data: CreateMaterialBorrowRequest) => Promise<MaterialBorrowResponse>
  /** 归还物料（员工发起/仓管直接确认） */
  returnMaterial: (id: number, data?: ReturnMaterialRequest) => Promise<MaterialBorrowResponse>
  /** 获取我的借用记录 */
  getMyBorrows: (params?: MyBorrowsQuery) => Promise<PaginationResult<MaterialBorrowResponse>>
  /** 获取待归还列表 */
  getPendingList: (params?: MaterialBorrowListQuery) => Promise<PaginationResult<MaterialBorrowResponse>>
  /** 获取借用统计 */
  getStatistics: (params?: { factory_id?: number; borrower_id?: number }) => Promise<MaterialBorrowStatisticsResponse>
  /** 仓管确认借用出库 */
  confirmBorrow: (id: number) => Promise<MaterialBorrowResponse>
  /** 仓管拒绝借用申请 */
  rejectBorrow: (id: number, reason?: string) => Promise<MaterialBorrowResponse>
  /** 仓管确认归还入库 */
  confirmReturn: (id: number, returnQuantity?: number) => Promise<MaterialBorrowResponse>
  /** 仓管拒绝归还申请 */
  rejectReturn: (id: number, reason?: string) => Promise<MaterialBorrowResponse>
}

/**
 * 创建物料借用 API 实例
 * 
 * @param client - HTTP 请求客户端
 * @returns 物料借用 API 实例
 * 
 * @example
 * ```typescript
 * import { createMaterialBorrowApi } from '@bayes-mes/shared/api/material-borrow'
 * 
 * const borrowApi = createMaterialBorrowApi(httpClient)
 * 
 * // 获取借用列表
 * const list = await borrowApi.getList({ pending: true })
 * 
 * // 创建借用
 * const borrow = await borrowApi.create({
 *   batch_id: 1,
 *   borrow_quantity: 10,
 *   borrow_reason: '生产测试使用'
 * })
 * 
 * // 归还物料
 * const returned = await borrowApi.returnMaterial(1, { return_quantity: 5 })
 * ```
 */
export function createMaterialBorrowApi(client: IRequestClient): MaterialBorrowApi {
  return {
    /**
     * 获取借用记录列表
     */
    getList: (params?: MaterialBorrowListQuery) => {
      return client.get<PaginationResult<MaterialBorrowResponse>>(
        ROUTES.list,
        { params }
      )
    },

    /**
     * 获取借用记录详情
     */
    getDetail: (id: number) => {
      return client.get<MaterialBorrowResponse>(
        ROUTES.detail(id)
      )
    },

    /**
     * 创建借用记录（出库）
     */
    create: (data: CreateMaterialBorrowRequest) => {
      return client.post<MaterialBorrowResponse>(
        ROUTES.create,
        data
      )
    },

    /**
     * 归还物料
     */
    returnMaterial: (id: number, data?: ReturnMaterialRequest) => {
      return client.post<MaterialBorrowResponse>(
        ROUTES.return(id),
        data || {}
      )
    },

    /**
     * 获取我的借用记录
     */
    getMyBorrows: (params?: MyBorrowsQuery) => {
      return client.get<PaginationResult<MaterialBorrowResponse>>(
        ROUTES.my,
        { params }
      )
    },

    /**
     * 获取待归还列表
     */
    getPendingList: (params?: MaterialBorrowListQuery) => {
      return client.get<PaginationResult<MaterialBorrowResponse>>(
        ROUTES.pending,
        { params }
      )
    },

    /**
     * 获取借用统计
     */
    getStatistics: (params?: { factory_id?: number; borrower_id?: number }) => {
      return client.get<MaterialBorrowStatisticsResponse>(
        ROUTES.statistics,
        { params }
      )
    },

    /**
     * 仓管确认借用出库
     */
    confirmBorrow: (id: number) => {
      return client.post<MaterialBorrowResponse>(
        ROUTES.confirm(id)
      )
    },

    /**
     * 仓管拒绝借用申请
     */
    rejectBorrow: (id: number, reason?: string) => {
      return client.post<MaterialBorrowResponse>(
        ROUTES.rejectApplication(id),
        { reason }
      )
    },

    /**
     * 仓管确认归还入库
     */
    confirmReturn: (id: number, returnQuantity?: number) => {
      return client.post<MaterialBorrowResponse>(
        ROUTES.confirmReturn(id),
        returnQuantity ? { return_quantity: returnQuantity } : {}
      )
    },

    /**
     * 仓管拒绝归还申请
     */
    rejectReturn: (id: number, reason?: string) => {
      return client.post<MaterialBorrowResponse>(
        ROUTES.rejectReturn(id),
        { reason }
      )
    },
  }
}
